import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, MapPin, CreditCard, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useStore } from "@/lib/store";
import { api, errorMessage } from "@/lib/api";
import { useCartQuote } from "@/pages/Cart";
import { Totals } from "@/components/Totals";

const PROVINCES = ["QC", "ON", "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "PE", "SK", "YT"];
const POSTAL = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const Field = ({ label, name, form, set, required, className = "", ...rest }) => (
  <label className={className}>
    <span className="label">{label}{required && " *"}</span>
    <input value={form[name]} onChange={(e) => set(name, e.target.value)} className="field" data-testid={`checkout-${name}-input`} required={required} {...rest} />
  </label>
);

export default function Checkout() {
  const { t, lang, money } = useLang();
  const { items, settings } = useStore();
  const quote = useCartQuote();
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", line1: "", line2: "", city: "", province: "QC", postal_code: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!items.length) return <Navigate to="/cart" replace />;

  const submit = async (e, method = "stripe") => {
    e.preventDefault();
    const req = ["name", "email", "phone", "line1", "city", "postal_code"];
    if (req.some((k) => !form[k].trim())) return toast.error(t("checkout.required"));
    if (!EMAIL.test(form.email.trim())) return toast.error(t("checkout.email_bad"));
    if (!POSTAL.test(form.postal_code.trim())) return toast.error(t("checkout.postal_bad"));
    setBusy(method);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map(({ upload_id, print_type, width, height, quantity, rush, cut, file_check, notes }) => ({ upload_id, print_type, width, height, quantity, rush, cut, file_check, notes })),
        customer: { name: form.name.trim(), business: form.business.trim(), email: form.email.trim(), phone: form.phone.trim() },
        shipping: { line1: form.line1.trim(), line2: form.line2.trim(), city: form.city.trim(), province: form.province, postal_code: form.postal_code.trim(), country: "CA" },
        language: lang,
        origin_url: window.location.origin,
        payment_method: method,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errorMessage(err, t));
      setBusy(null);
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-6xl px-5 py-12 lg:px-8" data-testid="checkout-page" noValidate>
      <h1 className="font-display text-4xl sm:text-5xl">{t("checkout.title")}</h1>
      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="flex flex-col gap-6">
          <section className="panel p-6">
            <h2 className="mb-5 text-lg font-extrabold">{t("checkout.contact")}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("checkout.name")} name="name" form={form} set={set} required autoComplete="name" />
              <Field label={t("checkout.business")} name="business" form={form} set={set} autoComplete="organization" />
              <Field label={t("checkout.email")} name="email" form={form} set={set} required type="email" autoComplete="email" />
              <Field label={t("checkout.phone")} name="phone" form={form} set={set} required type="tel" autoComplete="tel" />
            </div>
          </section>
          <section className="panel p-6">
            <h2 className="mb-5 text-lg font-extrabold">{t("checkout.ship_to")}</h2>
            <div className="grid gap-4 sm:grid-cols-6">
              <Field className="sm:col-span-6" label={t("checkout.line1")} name="line1" form={form} set={set} required autoComplete="address-line1" />
              <Field className="sm:col-span-6" label={t("checkout.line2")} name="line2" form={form} set={set} autoComplete="address-line2" />
              <Field className="sm:col-span-3" label={t("checkout.city")} name="city" form={form} set={set} required autoComplete="address-level2" />
              <label className="sm:col-span-1">
                <span className="label">{t("checkout.province")} *</span>
                <select value={form.province} onChange={(e) => set("province", e.target.value)} className="field" data-testid="checkout-province-select">
                  {PROVINCES.map((p) => <option key={p} value={p} className="bg-navy">{p}</option>)}
                </select>
              </label>
              <Field className="sm:col-span-2" label={t("checkout.postal")} name="postal_code" form={form} set={set} required autoComplete="postal-code" placeholder="H7T 0A1" />
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-slate-400"><MapPin className="h-4 w-4 text-ruby" /> {t("checkout.country")}</p>
          </section>
        </div>
        <div>
          {quote && (
            <Totals quote={quote}>
              <button type="submit" disabled={!!busy} className="btn-gold mt-6 w-full" data-testid="checkout-pay-btn">
                {busy === "stripe" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                {busy === "stripe" ? t("checkout.redirecting") : `${t("checkout.pay_card")} · ${money(quote.total)}`}
              </button>
              {settings?.paypal_enabled && (<>
              <div className="my-3 flex items-center gap-3 text-xs font-bold uppercase text-slate-500"><span className="h-px flex-1 bg-white/10" />{t("checkout.or")}<span className="h-px flex-1 bg-white/10" /></div>
              <button type="button" disabled={!!busy} onClick={(e) => submit(e, "paypal")} data-testid="checkout-paypal-btn"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#ffc439] px-6 py-3 font-extrabold text-[#003087] transition-[filter] hover:brightness-95 disabled:opacity-50">
                {busy === "paypal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
                {busy === "paypal" ? t("checkout.redirecting") : t("checkout.pay_paypal")}
              </button>
              <p className="mt-2 text-center text-xs text-slate-500">{t("checkout.paypal_tax")}</p>
              </>)}
              <p className="mt-3 text-center text-xs text-slate-500">{t("checkout.note")}</p>
            </Totals>
          )}
        </div>
      </div>
    </form>
  );
}
